import { Button, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDateRange } from '$shared/utils/date.utils';

import { Card } from '$components/card';
import { DeviceNameCell } from '$components/device/name-cell';
import { CopyHashCell } from '$features/report-hub/components/copy-hash-cell';
import {
  CollapsibleSection,
  type CollapsibleSectionHandle,
} from '$features/report-hub/stability-report/collapsible-section';
import { DoorStatsCard } from '$features/report-hub/stability-report/door-stats-card';
import { EventCountsRow } from '$features/report-hub/stability-report/event-counts-row';
import { ParameterSection } from '$features/report-hub/stability-report/parameter-section';
import { EventSeverityFilter } from '$features/report-hub/stability-report/severity-filter';
import { StabilityReportEvents } from '$features/report-hub/stability-report/stability-report-events';
import { StabilityReportHistoryCharts } from '$features/report-hub/stability-report/stability-report-history-charts';
import './stability-report-sidecar.scss';
import { StabilityReportDetailModel } from '$features/report-hub/stability-report/stability-report.types';
import { useCopyHash } from '$features/report-hub/use-copy-hash';

export interface StabilityReportDetailViewProps {
  report: StabilityReportDetailModel;
  onDelete: () => void;
  onDownload: () => void;
  isDownloading: boolean;
}

export function StabilityReportDetail({
  report,
  onDelete,
  onDownload,
  isDownloading,
}: StabilityReportDetailViewProps) {
  const { t } = useTranslation();
  const { copiedHashReportId, handleCopyHash } = useCopyHash();
  const eventsSectionRef = useRef<CollapsibleSectionHandle>(null);
  const eventsContainerRef = useRef<HTMLDivElement>(null);
  const [eventsSeverityFilter, setEventsSeverityFilter] = useState<
    EventSeverityFilter | undefined
  >();

  const handleEventTypeClick = useCallback((severity: EventSeverityFilter) => {
    setEventsSeverityFilter(severity);
    eventsSectionRef.current?.open();
    // Scroll after a tick so the section is expanded
    setTimeout(() => {
      eventsContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, []);

  return (
    <div>
      {/* Device + Period + Events card */}
      <Card className="m-bottom-l">
        <Card.Header>
          <div className="flex flex__jc--center p-l">
            <DeviceNameCell
              device={{
                name: report.deviceName ?? undefined,
                model: report.deviceModel ?? undefined,
                serialNumber: report.serialNumber ?? '',
                manufacturer: 'Eppendorf',
              }}
            />
          </div>
        </Card.Header>

        <div className="flex flex__ai--center flex__jc--center gap-s p-top-xl p-bottom-m">
          <Icon name="calendar" size={IconSizeClasses.XSmall} />
          <span>{formatDateRange(report.startDate, report.endDate, true)}</span>
        </div>

        <div className="flex flex__dir--column gap-s p-bottom-xl">
          <EventCountsRow
            eventCounts={report.eventCounts}
            onEventTypeClick={handleEventTypeClick}
          />
          <a
            href={`/monitoring?sidecarRoute=monitoring/parameters&manufacturer=${encodeURIComponent(report.manufacturer ?? 'Eppendorf')}&serialNumber=${encodeURIComponent(report.serialNumber ?? '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex__jc--center flex__ai--center gap-xs color-blue-500 font-size-s m-top-m"
            style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            {t('shared.viewMonitoringDetails')}
            <Icon
              name="external-link"
              size={IconSizeClasses.XSmall}
              className="bg-blue-500"
            />
          </a>
        </div>
      </Card>
      {/* Door section */}
      {report.door && <DoorStatsCard door={report.door} />}
      {/* Parameters sections */}
      {report.parameters?.map((parameter) => (
        <ParameterSection
          key={parameter.telemetryId ?? parameter.name}
          parameter={parameter}
        />
      ))}
      {/* Collapsible sections */}
      <CollapsibleSection
        iconName="charts"
        title={t('stabilityDetail.sections.historyCharts')}
      >
        <StabilityReportHistoryCharts report={report} />
      </CollapsibleSection>
      <div ref={eventsContainerRef}>
        <CollapsibleSection
          ref={eventsSectionRef}
          iconName="events"
          title={t('stabilityDetail.sections.events')}
        >
          <StabilityReportEvents
            reportId={report.id}
            initialSeverity={eventsSeverityFilter}
          />
        </CollapsibleSection>
      </div>
      <CollapsibleSection iconName="document" title={t('stabilityDetail.sections.notes')}>
        <div className="m-bottom-l">{report.notes}</div>
      </CollapsibleSection>
      {/* Metadata */}
      <div className="stability-sidecar__meta">
        <span className="font-weight-bold">
          {t('stabilityDetail.metadata.requestedBy')}
        </span>
        <span>
          {report.requestedBy} {t('stabilityDetail.metadata.on')}{' '}
          {new Date(report.createdAt).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
        {report.hash && (
          <>
            <span className="font-weight-bold">
              {t('stabilityDetail.metadata.sha256Hash')}
            </span>
            <CopyHashCell
              hash={report.hash}
              reportId={report.id}
              copiedHashReportId={copiedHashReportId}
              onCopy={handleCopyHash}
            />
          </>
        )}
      </div>
      {/* Footer actions */}
      <div className="stability-sidecar__footer">
        <Button
          className="stability-sidecar__delete-button"
          variant="tertiary"
          color="danger"
          size="medium"
          leadingIcon={
            <Icon name="delete" size={IconSizeClasses.XSmall} className="bg-red-500" />
          }
          onClick={onDelete}
        >
          {t('stabilityDetail.actions.delete')}
        </Button>
        {report.status === 'Ready' && (
          <Button size="medium" onClick={onDownload} disabled={isDownloading}>
            <Icon name="save" size={IconSizeClasses.XSmall} className="bg-white" />
            <span className="m-left-l">{t('stabilityDetail.actions.download')}</span>
          </Button>
        )}
      </div>
    </div>
  );
}
